import React, { useState, useRef, useEffect } from 'react';
import { Input } from './input';
import { Button } from './button';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { cn } from '../../lib/utils';

export function SearchableSelect({ 
  options = [], 
  value, 
  onValueChange, 
  placeholder = "Seleziona...",
  searchPlaceholder = "Cerca...",
  allowCustom = true,
  className,
  ...props
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showAddNew, setShowAddNew] = useState(false);
  const containerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setSearch('');
        setShowAddNew(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(option => {
    const searchText = search.toLowerCase();
    if (typeof option === 'string') {
      return option.toLowerCase().includes(searchText);
    }
    // Handle object format with value and label
    return option.label?.toLowerCase().includes(searchText) || 
           option.value?.toLowerCase().includes(searchText);
  });

  const handleSelect = (selectedValue) => {
    if (onValueChange) {
      onValueChange(selectedValue);
    }
    setOpen(false);
    setSearch('');
    setShowAddNew(false);
  };

  const handleAddNew = () => {
    if (search.trim() && onValueChange) {
      onValueChange(search.trim());
      setOpen(false);
      setSearch('');
      setShowAddNew(false);
    }
  };

  // Get display value - handle both string and object options
  const getDisplayValue = () => {
    if (!value) return '';
    
    const selectedOption = options.find(opt => {
      if (typeof opt === 'string') return opt === value;
      return opt.value === value;
    });
    
    if (selectedOption) {
      return typeof selectedOption === 'string' ? selectedOption : selectedOption.label;
    }
    
    return value;
  };

  const displayValue = getDisplayValue();

  return (
    <div ref={containerRef} className={cn("relative", className)} {...props}>
      <Button
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className="w-full justify-between"
        onClick={() => setOpen(!open)}
      >
        <span className={cn("truncate", !value && "text-muted-foreground")}>
          {displayValue || placeholder}
        </span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow-lg">
          <div className="p-2">
            <Input
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setShowAddNew(allowCustom && e.target.value.trim().length > 0 && 
                  !filteredOptions.some(opt => {
                    const optText = typeof opt === 'string' ? opt : opt.label;
                    return optText.toLowerCase() === e.target.value.toLowerCase();
                  }));
              }}
              className="mb-2"
              autoFocus
            />
          </div>

          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 && !showAddNew ? (
              <div className="py-6 text-center text-sm text-gray-500">
                Nessun risultato trovato
              </div>
            ) : (
              <>
                {filteredOptions.map((option) => {
                  const optionValue = typeof option === 'string' ? option : option.value;
                  const optionLabel = typeof option === 'string' ? option : option.label;
                  const isSelected = value === optionValue;
                  
                  return (
                    <button
                      key={optionValue}
                      type="button"
                      className={cn(
                        "relative flex w-full cursor-pointer select-none items-center rounded-sm py-2 px-3 text-sm outline-none hover:bg-gray-100",
                        isSelected && "bg-blue-50"
                      )}
                      onClick={() => handleSelect(optionValue)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          isSelected ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {optionLabel}
                    </button>
                  );
                })}
              </>
            )}

            {showAddNew && allowCustom && (
              <button
                type="button"
                className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-2 px-3 text-sm outline-none hover:bg-green-50 text-green-700 border-t border-gray-200"
                onClick={handleAddNew}
              >
                <Plus className="mr-2 h-4 w-4" />
                <span className="font-medium">Aggiungi "{search}"</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
